import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'bandedIntervalTrendLine',
  name_en: 'Banded Interval Trend Line',
  name_zh: '带状区间趋势折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y2: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y3: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y2: string;
    y3: string;
  };
}

export const bandedIntervalTrendLine = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const data = dataArray[spec.dataIndex!];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const { x, y, y2, y3 } = spec.encoding;

    // * ------------------------- data
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);

    const {
      x: xAxisData,
      y3: lowAttributeData,
      y2: upAttributeData,
      y: valueAttributeData
    } = util.dataEncoding.encoding(transformData, {
      x: { field: x },
      y3: {
        field: y3,
        transforms: [{ type: 'toNumeric' }]
      },
      y2: {
        field: y2,
        transforms: [{ type: 'toNumeric' }]
      },
      y: {
        field: y,
        transforms: [{ type: 'toNumeric' }]
      }
    });

    // * ------------------------- chart
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption
    };

    return {
      legend: {
        data: [y].map(item => ({
          name: item,
          type: util.const.SERIES_2_LEGEND_TYPE_MAP.line,
          selected: true
        }))
      },
      grid,
      dataZoom: presetOption.dataZoom,
      tooltip: {
        trigger: 'axis',
        formatter: params => {
          const series = Array.isArray(params) ? params : [params];
          const items = [];

          series.forEach(item => {
            // 筛去上下边界的折线数据
            if (item.seriesName !== y) {
              return;
            }

            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
              color: item.color,
              name: item.seriesName,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: series[0]?.axisValueLabel,
            items
          });
        }
      },
      xAxis: [
        {
          boundaryGap: false,
          show: true,
          type: 'category',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData
        }
      ],
      yAxis: [
        {
          show: true,
          dvSplitLineNumber: hook.getSplitLineNumber(chartViewRect.height - grid.top - grid.bottom),
          type: 'value',
          name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : '')
        }
      ],
      series: [
        {
          name: y,
          type: 'line',
          data: valueAttributeData
        },
        {
          // name: lowAttribute,
          type: 'line',
          lineStyle: {
            opacity: 0
          },
          stack: 'confidence-band',
          symbol: 'none',
          data: lowAttributeData
        },
        {
          // name: upAttribute,
          type: 'line',
          lineStyle: {
            opacity: 0
          },
          areaStyle: {
            opacity: 0.15
          },
          stack: 'confidence-band',
          symbol: 'none',
          data: upAttributeData
        }
      ]
    };
  }
});
